import { useState, useEffect } from "react";
import { Brain, RefreshCw, TrendingUp, TrendingDown, BarChart3, Calendar, Clock, Trophy, AlertTriangle } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

function StatBadge({ label, value, color = "slate" }) {
  return (
    <div className={`px-3 py-2 rounded-lg bg-${color}-500/10 border border-${color}-500/30`}>
      <div className={`text-[10px] uppercase tracking-wide text-${color}-400 font-bold`}>{label}</div>
      <div className={`font-mono font-bold text-${color}-300 text-base mt-0.5`}>{value}</div>
    </div>
  );
}

function MiniBar({ stat, isGood = (avg) => avg > 0 }) {
  const good = isGood(stat.avg);
  const color = stat.n === 0 ? "slate" : good ? "emerald" : "red";
  return (
    <div className="grid grid-cols-12 gap-2 items-center py-1 text-xs">
      <div className="col-span-3 font-mono text-slate-300 truncate">{stat.key}</div>
      <div className="col-span-2 text-right font-mono text-slate-500">n={stat.n}</div>
      <div className="col-span-2 text-right font-mono">
        <span className={stat.win_rate >= 50 ? "text-emerald-400" : stat.win_rate >= 35 ? "text-amber-400" : "text-red-400"}>
          {stat.win_rate.toFixed(0)}%
        </span>
      </div>
      <div className="col-span-2 text-right font-mono">
        <span className={`text-${color}-400`}>{stat.avg >= 0 ? "+" : ""}{stat.avg.toFixed(2)}%</span>
      </div>
      <div className="col-span-3 text-right font-mono">
        <span className={`text-${color}-400 font-bold`}>{stat.total >= 0 ? "+" : ""}{stat.total.toFixed(1)}%</span>
      </div>
    </div>
  );
}

export default function BotPerformanceAudit() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/bot/audit`, { timeout: 30000 });
      setData(r.data);
    } catch (e) {
      console.error("Bot audit error", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  if (loading && !data) {
    return (
      <div className="glass-card rounded-xl p-5 border border-purple-500/30">
        <div className="flex items-center gap-2 text-purple-300 text-sm">
          <RefreshCw className="w-4 h-4 animate-spin" /> Audit du bot…
        </div>
      </div>
    );
  }

  if (!data?.ok) {
    return (
      <div className="glass-card rounded-xl p-5 border border-slate-700">
        <div className="text-slate-400 text-sm">{data?.message || "Pas encore de trades."}</div>
      </div>
    );
  }

  const o = data.overall;
  const isProfitable = o.profit_factor >= 1.5;
  const beatingSP = o.total >= 8; // S&P ~8% benchmark

  return (
    <div className="glass-card rounded-xl border border-purple-500/30 overflow-hidden" data-testid="bot-audit-panel">
      <div className="bg-gradient-to-r from-purple-500/15 via-fuchsia-500/10 to-transparent px-5 py-4 border-b border-slate-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Brain className="w-5 h-5 text-purple-400" />
          <div>
            <h3 className="font-bold text-white text-base">Audit Performance — Data-Driven</h3>
            <p className="text-xs text-slate-400">{data.round_trips} round-trips • {data.strategy_version}</p>
          </div>
        </div>
        <button onClick={fetchData} disabled={loading}
          className="text-slate-400 hover:text-white transition-colors disabled:opacity-40"
          data-testid="bot-audit-refresh">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* Headline KPIs */}
      <div className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-2 border-b border-slate-800">
        <StatBadge label="Win Rate" value={`${o.win_rate}%`}
          color={o.win_rate >= 50 ? "emerald" : o.win_rate >= 40 ? "amber" : "red"} />
        <StatBadge label="Profit Factor" value={o.profit_factor.toFixed(2)}
          color={o.profit_factor >= 1.5 ? "emerald" : o.profit_factor >= 1.1 ? "amber" : "red"} />
        <StatBadge label="Avg / trade" value={`${o.avg >= 0 ? "+" : ""}${o.avg.toFixed(2)}%`}
          color={o.avg > 0.3 ? "emerald" : o.avg > 0 ? "amber" : "red"} />
        <StatBadge label="Total Return" value={`${o.total >= 0 ? "+" : ""}${o.total.toFixed(1)}%`}
          color={beatingSP ? "emerald" : o.total > 0 ? "amber" : "red"} />
      </div>

      {/* Verdict banner */}
      <div className={`px-5 py-3 ${isProfitable ? "bg-emerald-500/10 border-emerald-500/20" : "bg-amber-500/10 border-amber-500/20"} border-b text-xs flex items-center gap-2`}>
        {isProfitable
          ? <><Trophy className="w-4 h-4 text-emerald-400" /><span className="text-emerald-300">Bot rentable (PF ≥ 1.5). Continue.</span></>
          : <><AlertTriangle className="w-4 h-4 text-amber-400" /><span className="text-amber-300">PF &lt; 1.5 — pas encore robustement rentable. Reste en demo.</span></>}
        <span className="ml-auto text-slate-500">Win moyen +{o.avg_win}% • Loss moyen {o.avg_loss}%</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-slate-800">
        {/* By Mode */}
        <div className="p-4">
          <div className="flex items-center gap-2 mb-3 text-xs font-bold text-slate-400 uppercase tracking-wider">
            <BarChart3 className="w-3.5 h-3.5" /> Par Mode
          </div>
          <div className="grid grid-cols-12 gap-2 text-[10px] text-slate-500 font-bold mb-1 px-1">
            <div className="col-span-3">MODE</div>
            <div className="col-span-2 text-right">N</div>
            <div className="col-span-2 text-right">WR</div>
            <div className="col-span-2 text-right">AVG</div>
            <div className="col-span-3 text-right">TOTAL</div>
          </div>
          {data.by_mode.map((m) => <MiniBar key={m.key} stat={m} />)}
        </div>

        {/* By Day of Week */}
        <div className="p-4">
          <div className="flex items-center gap-2 mb-3 text-xs font-bold text-slate-400 uppercase tracking-wider">
            <Calendar className="w-3.5 h-3.5" /> Par Jour de Semaine
          </div>
          <div className="grid grid-cols-12 gap-2 text-[10px] text-slate-500 font-bold mb-1 px-1">
            <div className="col-span-3">JOUR</div>
            <div className="col-span-2 text-right">N</div>
            <div className="col-span-2 text-right">WR</div>
            <div className="col-span-2 text-right">AVG</div>
            <div className="col-span-3 text-right">TOTAL</div>
          </div>
          {data.by_dow.map((m) => <MiniBar key={m.key} stat={m} />)}
        </div>

        {/* By Hold Time */}
        <div className="p-4">
          <div className="flex items-center gap-2 mb-3 text-xs font-bold text-slate-400 uppercase tracking-wider">
            <Clock className="w-3.5 h-3.5" /> Par Durée de Détention
          </div>
          <div className="grid grid-cols-12 gap-2 text-[10px] text-slate-500 font-bold mb-1 px-1">
            <div className="col-span-3">HOLD</div>
            <div className="col-span-2 text-right">N</div>
            <div className="col-span-2 text-right">WR</div>
            <div className="col-span-2 text-right">AVG</div>
            <div className="col-span-3 text-right">TOTAL</div>
          </div>
          {data.by_hold_time.map((m) => <MiniBar key={m.key} stat={m} />)}
        </div>

        {/* By Grade */}
        <div className="p-4">
          <div className="flex items-center gap-2 mb-3 text-xs font-bold text-slate-400 uppercase tracking-wider">
            <Trophy className="w-3.5 h-3.5" /> Par Grade
          </div>
          <div className="grid grid-cols-12 gap-2 text-[10px] text-slate-500 font-bold mb-1 px-1">
            <div className="col-span-3">GRADE</div>
            <div className="col-span-2 text-right">N</div>
            <div className="col-span-2 text-right">WR</div>
            <div className="col-span-2 text-right">AVG</div>
            <div className="col-span-3 text-right">TOTAL</div>
          </div>
          {data.by_grade.map((m) => <MiniBar key={m.key} stat={m} />)}
        </div>
      </div>

      {/* Top winners / losers */}
      <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-slate-800 border-t border-slate-800">
        <div className="p-4">
          <div className="flex items-center gap-2 mb-2 text-xs font-bold text-emerald-400 uppercase tracking-wider">
            <TrendingUp className="w-3.5 h-3.5" /> Top 5 Gagnants
          </div>
          <div className="space-y-1">
            {data.top_winners.slice(0, 5).map((r, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className="font-mono font-bold text-white w-16 truncate">{r.ticker}</span>
                <span className="text-slate-500 text-[10px] flex-1 truncate">{r.mode}</span>
                <span className="text-emerald-400 font-mono font-bold">+{r.pnl_pct.toFixed(2)}%</span>
              </div>
            ))}
          </div>
        </div>
        <div className="p-4">
          <div className="flex items-center gap-2 mb-2 text-xs font-bold text-red-400 uppercase tracking-wider">
            <TrendingDown className="w-3.5 h-3.5" /> Top 5 Perdants
          </div>
          <div className="space-y-1">
            {data.top_losers.slice(0, 5).map((r, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className="font-mono font-bold text-white w-16 truncate">{r.ticker}</span>
                <span className="text-slate-500 text-[10px] flex-1 truncate">{r.mode}</span>
                <span className="text-red-400 font-mono font-bold">{r.pnl_pct.toFixed(2)}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
